import React, { useEffect, useState, useRef } from 'react'
import heic2any from 'heic2any'
import API from '../../services/API'
import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { CloudUpload, Description } from '@mui/icons-material'
import BigModal from '../Modals/BigModal'
import SmallModal from '../Modals/SmallModal'
import AttachmentPreviewModal from './AttachmentPreviewModal'
import AttachmentPreview from './AttachmentPreview'

const getFileType = (url) => {
  const cleanUrl = url.split('?')[0].toLowerCase() // Remove query params
  if (cleanUrl.endsWith('.pdf')) return 'pdf'
  if (cleanUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) return 'image'
  return 'other'
}

const Attachments = ({
  s3folder,
  actionAfterDelete,
  actionAfterUpload,
  existingAttachments,
  hideTitle,
}) => {
  const [attachments, setAttachments] = useState(existingAttachments ?? [])
  const [loading, setLoading] = useState(false)
  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const [openUpload, setOpenUpload] = useState(false)
  const [addNewFile, setAddNewFile] = useState(null)
  const [uploadingLoading, setUploadingLoading] = useState(false)

  const fileInputRef = useRef(null)

  const handleUpload = async () => {
    setUploadingLoading(true)

    let imageForUpload = addNewFile[0]

    const isHeic =
      addNewFile[0].type === 'image/heic' ||
      addNewFile[0].type === 'image/heif' ||
      /\.heic$/i.test(addNewFile[0].name) ||
      /\.heif$/i.test(addNewFile[0].name)

    if (isHeic) {
      try {
        const converted = await heic2any({
          blob: addNewFile[0],
          toType: 'image/jpeg',
          quality: 0.85,
        })
        // heic2any may return Blob or Blob[]
        const jpegBlob = Array.isArray(converted) ? converted[0] : converted

        imageForUpload = new File(
          [jpegBlob],
          image.name.replace(/\.(heic|heif)$/i, '.jpg'),
          {
            type: 'image/jpeg',
          },
        )
      } catch (err) {
        console.error('HEIC conversion failed', err)
        return
      }
    }

    await API.post('/gets3uploadUrl', {
      folder: s3folder,
      extension: imageForUpload.type.split('/')[1],
    }).then((res) => {
      let s3url = res.data.url
      let s3key = res.data.key
      API.put(s3url, imageForUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then((res) => {
        if (res.status === 200) {
          actionAfterUpload(s3key)
          setAttachments((prev) => [...prev, s3key])
          setAddNewFile(null)
          setOpenUpload(false)
          setUploadingLoading(false)
        }
      })
    })
  }

  const handleDelete = (s3key) => {
    API.post('/deletes3object', { key: s3key }).then((res) => {
      if (res.status === 200) {
        setAttachments((prev) => prev.filter((x) => x !== s3key))
        actionAfterDelete()
      }
    })
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {(!hideTitle || actionAfterUpload) && (
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="title2">
                {hideTitle ? '' : 'Attachments'}
              </Typography>
              {/* TOOLBAR */}
              <Box justifyContent="flex-end">
                <ButtonGroup>
                  {actionAfterUpload && (
                    <IconButton
                      onClick={() => setOpenUpload(true)}
                      color="anBlue"
                    >
                      <CloudUpload />
                    </IconButton>
                  )}
                </ButtonGroup>
              </Box>
            </Stack>
          </Grid>
        )}
        {attachments.map((x) => (
          <AttachmentPreview
            key={x}
            s3key={x}
            onClick={(s3key) => setSelectedAttachment(s3key)}
          />
        ))}
      </Grid>
      <SmallModal open={openUpload} onClose={() => setOpenUpload(false)}>
        <Stack gap={1}>
          <Typography variant="title3" textAlign="center">
            Upload a File
          </Typography>
          {/* Hidden real input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (!file) return

              const MAX_SIZE = 20 * 1024 * 1024 // 20 MB

              if (file.size > MAX_SIZE) {
                alert('File is too large. Maximum allowed size is 20 MB.')
                e.target.value = '' // reset file input
                setAddNewFile(null)
                return
              }

              // File OK → update state
              setAddNewFile(e.target.files)
            }}
          />

          {/* Pretty upload box */}
          <Box
            sx={{
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'grey.400',
              bgcolor: 'grey.50',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'anBlue.main',
                bgcolor: 'grey.100',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 32, mb: 1, color: 'anBlue.main' }} />
            <Typography variant="body2" fontWeight={600}>
              {addNewFile ? 'Change file' : 'Click to choose a file'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PDF or image
            </Typography>

            {addNewFile && (
              <Typography
                variant="caption"
                sx={{ mt: 1 }}
                color="text.secondary"
                // noWrap
              >
                Selected: {addNewFile[0]?.name}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            color="success"
            fullWidth
            sx={{ mt: 1 }}
            onClick={handleUpload}
            disabled={!addNewFile || uploadingLoading}
          >
            {uploadingLoading ? 'Uploading…' : 'Upload'}
          </Button>
        </Stack>
      </SmallModal>
      <AttachmentPreviewModal
        open={selectedAttachment}
        onClose={() => setSelectedAttachment(null)}
        s3key={selectedAttachment}
        onDelete={actionAfterDelete ? handleDelete : null}
      />
    </Box>
  )
}

export default Attachments
